import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useResources, useResourceSearch, useSyncResources } from '../hooks'
import { ResourceCard } from './ResourceCard'
import { RESOURCE_TYPE } from '../types'
import type { ResourceItem, ResourceType } from '../types'
import { SkeletonCard, EmptyState } from '@/shared/components'
import { IconImage, IconSync, IconX } from '@/app/components/icons'

const FILTER_TYPES = ['all', ...Object.values(RESOURCE_TYPE)] as const

interface ResourceGridProps {
  readonly selectedType: ResourceType | null
  readonly onSelectResource: (item: ResourceItem) => void
}

export function ResourceGrid({ selectedType, onSelectResource }: ResourceGridProps) {
  const { t } = useTranslation()
  const [typeFilter, setTypeFilter] = useState<ResourceType | null>(selectedType)
  const [searchQuery, setSearchQuery] = useState('')

  const resourcesQuery = useResources(typeFilter ?? undefined)
  const searchQueryResult = useResourceSearch(searchQuery, typeFilter ?? undefined)
  const syncMutation = useSyncResources()

  const isSearching = searchQuery.length > 0
  const activeQuery = isSearching ? searchQueryResult : resourcesQuery
  const items = isSearching
    ? (searchQueryResult.data?.items ?? [])
    : (resourcesQuery.data?.items ?? [])

  return (
    <div className="flex flex-col gap-4">
      {/* Search input */}
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('resources.searchPlaceholder')}
          className="w-full rounded-xl border border-tg-hint/20 px-3 py-2.5 text-sm outline-none transition-all duration-150 focus:border-[var(--tg-theme-button-color)] focus:shadow-sm bg-tg-secondary text-tg-text placeholder:text-tg-hint hover:border-tg-hint/40"
        />
      </div>

      {/* Type filter pills */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {FILTER_TYPES.map((filterType) => {
          const isAll = filterType === 'all'
          const isActive = isAll ? typeFilter === null : typeFilter === filterType
          const label = isAll
            ? t('resources.filter.all')
            : t(`resources.filter.${filterType}`)

          return (
            <button
              key={filterType}
              type="button"
              onClick={() => setTypeFilter(isAll ? null : filterType as ResourceType)}
              className="shrink-0 rounded-full px-3 py-1.5 text-sm font-medium transition-all duration-150 active:scale-[0.96]"
              style={{
                backgroundColor: isActive
                  ? 'var(--tg-theme-button-color)'
                  : 'color-mix(in srgb, var(--tg-theme-button-color) 8%, var(--tg-theme-secondary-bg-color))',
                color: isActive
                  ? 'var(--tg-theme-button-text-color)'
                  : 'var(--tg-theme-text-color)',
                border: isActive
                  ? '1px solid var(--tg-theme-button-color)'
                  : '1px solid transparent',
              }}
            >
              {label}
            </button>
          )
        })}
      </div>

      {/* Sync button */}
      <div className="flex justify-end">
        <button
          type="button"
          disabled={syncMutation.isPending}
          onClick={() => syncMutation.mutate()}
          className="flex items-center gap-2 rounded-xl bg-gradient-to-br from-tg-button to-tg-button/80 px-3 py-2 text-sm font-medium text-tg-button-text shadow-sm shadow-tg-button/20 transition-all duration-200 disabled:opacity-50 active:scale-[0.98]"
        >
          {syncMutation.isPending && (
            <IconSync className="h-4 w-4 animate-spin" />
          )}
          {syncMutation.isPending ? t('resources.syncing') : t('resources.sync')}
        </button>
      </div>

      {/* Sync success message */}
      {syncMutation.isSuccess && (
        <div className="animate-fade-in rounded-lg border border-tg-hint/10 bg-tg-secondary px-3 py-2 text-sm" style={{ color: 'var(--tg-theme-button-color, #3b82f6)' }}>
          {t('resources.syncSuccess')}
        </div>
      )}

      {/* Loading state */}
      {activeQuery.isLoading && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} variant="wide" />
          ))}
        </div>
      )}

      {/* Error state */}
      {activeQuery.isError && (
        <EmptyState
          icon={<IconX className="h-7 w-7 text-tg-destructive" />}
          title={t('common.error')}
        />
      )}

      {/* Empty state */}
      {!activeQuery.isLoading && !activeQuery.isError && items.length === 0 && (
        <EmptyState
          icon={<IconImage className="h-8 w-8" />}
          title={t('resources.emptyTitle')}
          description={t('resources.emptyDesc')}
          action={
            <button
              type="button"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
              className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all duration-200 active:scale-[0.98] bg-tg-button text-tg-button-text disabled:opacity-50"
            >
              <IconSync className="h-4 w-4" />
              {t('resources.sync')}
            </button>
          }
        />
      )}

      {/* Grid */}
      {items.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {items.map((item) => (
            <ResourceCard key={item.id} item={item} onClick={() => onSelectResource(item)} />
          ))}
        </div>
      )}
    </div>
  )
}
